<template>
  <section class="SB-datepicker">
    <multiselect
      v-model="selectedShortcut"
      v-select-overflow
      placeholder="Select an option"
      class="flex-options hide-tags no-selected-background right-align sb2-bordered sb2-hoverable sb2-focusable"
      :options="dateShortcuts"
      label="text"
      track-by="value"
      show
      :multiple="false"
      :show-labels="false"
      :preselect-first="true"
      :show-no-results="false"
      :clear-on-select="false"
      :preserve-search="true"
      :allow-empty="false"
      :searchable="false" >
      <template
        slot="selection"
        slot-scope="{ values }">
        <span
          v-if="values.length"
          class="multiselect__single" >{{ selectedDateName }}</span>
      </template>
      <template
        slot="option"
        slot-scope="props">
        <SBIcon
          :icon="SBIconList.DONE_TICK"
          :color="SBColors.bluePrimary"
          :size="9"/>
        <span class="greedy">{{ props.option.text }}</span>
      </template>
    </multiselect>

    <div
      class="date-picker-wrapper SB-shadow"
      :class="{hide: monthMode}">
      <el-date-picker
        ref="datePicker"
        v-model="dateRange"
        v-click-outside="closeDatePicker"
        type="daterange"
        align="left"
        :format="datePickerSelectedFormat"
        start-placeholder="Start date"
        end-placeholder="End date"
        class=""
        :editable="false"
        :picker-options="pickerOptions"
        @change="setRangeShortcutName"
        @mousedown.native="datePickerActive = true"
        @mouseup.native="datePickerActive = false" />
    </div>

    <div
      class="date-picker-wrapper SB-shadow"
      :class="{hide: !monthMode}">
      <el-date-picker
        ref="datePickerMonth"
        v-model="dateRange"
        v-click-outside="closeMonthPicker"
        type="monthrange"
        align="left"
        format="MMM yyyy"
        start-placeholder="Start date"
        end-placeholder="End date"
        :editable="false"
        @change="setRangeShortcutName"
        @mousedown.native="datePickerActive = true"
        @mouseup.native="datePickerActive = false" />
    </div>

    <div
      class="datepicker-active-shadow"
      :class="{'is-active': datePickerActive}" />
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { DatePicker } from 'element-ui';

import Multiselect from 'vue-multiselect';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import getFormat from '@/helpers/lists/countryTimeFormats';

/* eslint-disable no-underscore-dangle */
export default {
  name: 'DatePicker',
  components: {
    Multiselect,
    SBIcon,
    elDatePicker: DatePicker,
  },
  props: {
    defaultDate: {
      type: String,
      default: '',
    },
    omitShortcuts: {
      type: Array,
      default: () => [],
    },
    startDate: {
      type: String,
      required: false,
      default: null,
    },
    weekMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    monthMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      pickerOptions: {
        firstDayOfWeek: 1,
        onPick: this.onWeekPick,
      },
      weekRange: null, // TODO
      tempFirstWeekStartDate: null,
      tempFirstWeekEndDate: null,
      triggerChangedEvent: true,
      dateRange: [],
      selectedDateName: 'Select Date',
      selectedShortcut: null,
      addCustomToList: false,
      showCustom: false,
      datePickerActive: false,
      datePickerCellElements: null,
      datePickerRowElements: null,
      datePickerCurrentHoverEndWeek: null,
      datePickerCurrentHoverStartWeek: null,
      firstWeekSelected: null,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      userRegion: 'user/region',
      datePickerShortcut: 'helpers/datePickerShortcut',
      datePickerStartDate: 'helpers/datePickerStartDate',
      datePickerEndDate: 'helpers/datePickerEndDate',
    }),
    datePickerSelectedFormat() {
      return getFormat(this.userRegion).dateFull.replace('YYYY', 'yyyy').replace('D', 'd');
    },
    dateShortcuts() {
      const shortcuts = [
        {
          text: 'Today',
          value: 'today',
        },
        {
          text: 'This week',
          value: 'thisWeek',
        },
        {
          text: 'Last Week',
          value: 'lastWeek',
        },
        {
          text: 'This month',
          value: 'thisMonth',
        },
        {
          text: 'Last Month',
          value: 'lastMonth',
        },
        {
          text: 'This Year',
          value: 'thisYear',
        },
        {
          text: 'All time',
          value: 'allTime',
        },
      ];

      if (this.addCustomToList && this.showCustom) {
        shortcuts.push({
          text: 'Custom',
          value: 'custom',
        });
      } else if (this.addCustomToList && shortcuts[shortcuts.length - 1].value === 'custom') {
        shortcuts.pop();
      }

      return shortcuts.filter(shortcut => !this.omitShortcuts.includes(shortcut.value));
    },
  },
  watch: {
    dateRange(range) {
      this.setDatePickerRange(range);
      if (range.length && !range[0]._isAMomentObject) {
        this.triggerChangedEvent = false;
        this.dateRange = [
          this.$moment(range[0]),
          this.$moment(range[1]),
        ];
      }

      if (range.length && this.triggerChangedEvent) {
        this.$emit('datesChanged', range);
      }
      this.triggerChangedEvent = true;

      const inputs = document.getElementsByClassName('el-range-input');
      // addapt input width
      for (let i = 0; i < inputs.length; i++) {
        inputs[i].style.width = '1px';
        this.$nextTick(() => {
          inputs[i].style.width = `${inputs[i].scrollWidth}px`;

          // set the blue shadow size to fit
          const datePickers = this.$el.getElementsByClassName('date-picker-wrapper');
          let widestSize = 0;
          for (let i = 0; i < datePickers.length; i++) {
            if (datePickers[i].offsetWidth > widestSize) {
              widestSize = datePickers[i].offsetWidth;
            }
          }
          const shadowElement = this.$el.getElementsByClassName('datepicker-active-shadow');
          shadowElement[0].style.width = `${widestSize}px`;
        });
      }
    },
    firstWeekSelected() {
      if (!this.firstWeekSelected) {
        document.body.classList.add('date-picker-initial-mode');
      } else {
        document.body.classList.remove('date-picker-initial-mode');
      }
    },
    weekMode() {
      if (this.weekMode) {
        this.dateRange[0].startOf('week');
        this.dateRange[1].endOf('week');
        this.dateRange = [...this.dateRange];
        this.$emit('datesChanged', this.dateRange);
        document.body.classList.add('date-picker-week');
        setTimeout(() => {
          // show date picker just so it gets created in the DOM and event listeners can be set
          this.$refs.datePicker.showPicker();
          // then immediatelly delete it so it is not visible by default
          this.$refs.datePicker.hidePicker();
          
          this.$nextTick(() => {
            // Use the last available picker from the dom. The one active always moves to the end and we have max two pickers if moth mode has been used.
            const pickerPanels = document.getElementsByClassName('el-picker-panel');
            this.datePickerRowElements = pickerPanels.length === 1 ? pickerPanels[0].getElementsByClassName('el-date-table__row') : pickerPanels[1].getElementsByClassName('el-date-table__row');
            this.datePickerCellElements = pickerPanels.length === 1 ? pickerPanels[0].querySelectorAll('td') : pickerPanels[1].querySelectorAll('td');
            this.datePickerButtons = document.getElementsByClassName('el-picker-panel__icon-btn');
            this.startListeningOnWeekDate();
          });
        });
      } else {
        document.body.classList.remove('date-picker-week');
        this.removeAllListeners();
      }

      if (!this.monthMode && !this.weekMode) {
        // just to trigger day mode change
        this.dateRange = [...this.dateRange];
      }
    },
    monthMode() {
      if (this.monthMode) {
        if (this.dateRange[0]) {
          this.dateRange[0].startOf('month');
        }
        if (this.dateRange[1]) {
          this.dateRange[1].endOf('month');
        }
        
        this.dateRange = [...this.dateRange];
        setTimeout(() => {
          // show date picker just so it gets created in the DOM and event listeners can be set
          this.$refs.datePickerMonth.showPicker();
          // then immediatelly delete it so it is not visible by default
          this.$refs.datePickerMonth.hidePicker();
        });
      } else if (!this.monthMode && !this.weekMode) {
        // just to trigger day mode change
        this.dateRange = [...this.dateRange];
      }
    },
    selectedShortcut() {
      this.setDatePickerShortcut(this.selectedShortcut.value);
      if (this.selectedShortcut.value !== 'custom') {
        this.showCustom = false;
      }
      this.shortCutDateSelect(this.selectedShortcut.value);
    },
    startDate() {
      this.selectedShortcut = this.dateShortcuts[0];
    },
    datePickerActive() {
      if (this.datePickerActive) {
        this.datePickerCurrentHoverStartWeek = undefined;
        this.datePickerCurrentHoverEndWeek = undefined;
        this.$nextTick(() => {
          this.redrawSelection();
        });
      }
    },
  },
  beforeMount() {
    if (this.datePickerShortcut) {
      this.selectedShortcut = this.dateShortcuts.find(shortcut => shortcut.value === this.datePickerShortcut);
    }
  },
  mounted() {
    if (this.datePickerShortcut === 'custom') {
      this.setDateRange();
      this.setRangeShortcutName();
    }
    if (this.defaultDate) {
      if (this.$lodash.has(this.dateShortcuts, this.defaultDate)) {
        this.shortCutDateSelect(this.defaultDate);
      } else {
        throw Error('Wrong date shortcut');
      }
    }

    this.firstWeekSelected = false;
  },
  beforeDestroy() {
    this.removeAllListeners();
  },
  methods: {
    ...mapActions({
      setDatePickerShortcut: 'helpers/setDatePickerShortcut',
      setDatePickerRange: 'helpers/setDatePickerRange',
    }),
    startListeningOnWeekDate() {
      // Set listeners for all the rows to recalculate inbetweens
      for (let i = 0; i < this.datePickerRowElements.length; i++) {
        this.datePickerRowElements[i].addEventListener('mouseenter', this.mouseEnterDatePickerWeek);
      }
      // Set listeners for all the cels to set the current hover date
      for (let i = 0; i < this.datePickerCellElements.length; i++) {
        this.datePickerCellElements[i].addEventListener('mouseenter', this.mouseEnterDatePickerDay);
      }
      for (let i = 0; i < this.datePickerButtons.length; i++) {
        this.datePickerButtons[i].addEventListener('click', this.redrawSelection);
      }
    },
    removeAllListeners() {
      if (this.datePickerRowElements && this.datePickerRowElements.length) {
        for (let i = 0; i < this.datePickerRowElements.length; i++) {
          this.datePickerRowElements[i].removeEventListener('mouseenter', this.mouseEnterDatePickerWeek);
        }
      }
      if (this.datePickerRowElements && this.datePickerRowElements.length) {
        for (let i = 0; i < this.datePickerCellElements.length; i++) {
          this.datePickerCellElements[i].removeEventListener('mouseenter', this.mouseEnterDatePickerDay);
        }
      }
      if (this.datePickerRowElements && this.datePickerRowElements.length) {
        for (let i = 0; i < this.datePickerButtons.length; i++) {
          this.datePickerButtons[i].removeEventListener('click', this.redrawSelection);
        }
      }
    },
    // Set datePickerCurrentHover moment value
    mouseEnterDatePickerDay(event) {
      if (!this.weekMode) {
        return;
      }
      const pickerPanelSide = event.path.find(element => element.classList.contains('el-picker-panel__content'));
      if (pickerPanelSide) {
        const [hoverYear, hoverMonth] = pickerPanelSide.children[0].children[2].innerText.trim().split(' ');
        const hoverDate = event.target.children[0].children[0].innerText.trim();

        this.datePickerCurrentHoverStartWeek = this.$moment().set('year', hoverYear).set('month', hoverMonth);
        if (event.target.classList.contains('prev-month')) {
          this.datePickerCurrentHoverStartWeek.subtract(1, 'months');
        }
        if (event.target.classList.contains('next-month')) {
          this.datePickerCurrentHoverStartWeek.add(1, 'months');
        }
        this.datePickerCurrentHoverStartWeek.set('date', hoverDate);
        this.datePickerCurrentHoverStartWeek = this.datePickerCurrentHoverStartWeek.startOf('week');

        this.datePickerCurrentHoverEndWeek = this.$moment().set('year', hoverYear).set('month', hoverMonth);
        if (event.target.classList.contains('prev-month')) {
          this.datePickerCurrentHoverEndWeek.subtract(1, 'months');
        }
        if (event.target.classList.contains('next-month')) {
          this.datePickerCurrentHoverEndWeek.add(1, 'months');
        }
        this.datePickerCurrentHoverEndWeek.set('date', hoverDate);
        this.datePickerCurrentHoverEndWeek = this.datePickerCurrentHoverEndWeek.endOf('week');
      }
    },
    mouseEnterDatePickerWeek(event) {
      if (!this.weekMode) {
        return;
      }
      setTimeout(() => {
        const visibelPickerPanel = this.findVisiblePickerPanel();
        if (this.tempFirstWeekStartDate) {
          const pickerPanelContent = visibelPickerPanel.getElementsByClassName('el-picker-panel__content');
          const dateElementsLeft = pickerPanelContent[0].getElementsByClassName('available');
          const [yearLeft, monthLeft] = pickerPanelContent[0].children[0].children[2].innerText.trim().split(' ');
          const dateElementsRight = pickerPanelContent[1].getElementsByClassName('available');
          const [yearRight, monthRight] = pickerPanelContent[1].children[0].children[2].innerText.trim().split(' ');

          for (let i = 0; i < dateElementsLeft.length; i++) {
            const testDate = dateElementsLeft[i].innerText.trim();
            const testMoment = this.$moment().set('year', yearLeft).set('month', monthLeft).set('date', testDate);
            const isBetween = testMoment.isBetween(this.tempFirstWeekStartDate, this.datePickerCurrentHoverEndWeek);
            const isBetweenReverse = testMoment.isBetween(this.datePickerCurrentHoverStartWeek, this.tempFirstWeekStartDate);

            const hoverAfterSelectedFirstWeek = this.datePickerCurrentHoverStartWeek.isSameOrAfter(this.tempFirstWeekStartDate);
            const isBetweenStartWeek = testMoment.isBetween(this.tempFirstWeekStartDate, this.tempFirstWeekEndDate);
            const isBetweenEndWeek = testMoment.isBetween(this.datePickerCurrentHoverStartWeek, this.datePickerCurrentHoverEndWeek);
            
            if (isBetweenStartWeek && isBetweenEndWeek) {
              dateElementsLeft[i].parentNode.classList.add('sb-first-week');
              dateElementsLeft[i].parentNode.classList.add('sb-last-week');
            } else if ((isBetweenStartWeek && hoverAfterSelectedFirstWeek) || (isBetweenEndWeek && !hoverAfterSelectedFirstWeek)) {
              dateElementsLeft[i].parentNode.classList.add('sb-first-week');
              dateElementsLeft[i].parentNode.classList.remove('sb-last-week');
            } else if ((isBetweenStartWeek && !hoverAfterSelectedFirstWeek) || (isBetweenEndWeek && hoverAfterSelectedFirstWeek)) {
              dateElementsLeft[i].parentNode.classList.remove('sb-first-week');
              dateElementsLeft[i].parentNode.classList.add('sb-last-week');
            } else if (isBetween || isBetweenReverse) {
              dateElementsLeft[i].parentNode.classList.add('sb-between-week');
              dateElementsLeft[i].parentNode.classList.remove('sb-first-week');
              dateElementsLeft[i].parentNode.classList.remove('sb-last-week');
            } else {
              dateElementsLeft[i].parentNode.classList.remove('sb-first-week');
              dateElementsLeft[i].parentNode.classList.remove('sb-last-week');
              dateElementsLeft[i].parentNode.classList.remove('sb-between-week');
            }
            dateElementsLeft[i].parentNode.classList.remove('sb-hover-week');
          }

          for (let i = 0; i < dateElementsRight.length; i++) {
            const testDate = dateElementsRight[i].innerText.trim();
            const testMoment = this.$moment().set('year', yearRight).set('month', monthRight).set('date', testDate);
            const isBetween = testMoment.isBetween(this.tempFirstWeekStartDate, this.datePickerCurrentHoverEndWeek);
            const isBetweenReverse = testMoment.isBetween(this.datePickerCurrentHoverStartWeek, this.tempFirstWeekStartDate);

            const hoverAfterSelectedFirstWeek = this.datePickerCurrentHoverStartWeek.isSameOrAfter(this.tempFirstWeekStartDate);
            const isBetweenStartWeek = testMoment.isBetween(this.tempFirstWeekStartDate, this.tempFirstWeekEndDate);
            const isBetweenEndWeek = testMoment.isBetween(this.datePickerCurrentHoverStartWeek, this.datePickerCurrentHoverEndWeek);
            
            if (isBetweenStartWeek && isBetweenEndWeek) {
              dateElementsRight[i].parentNode.classList.add('sb-first-week');
              dateElementsRight[i].parentNode.classList.add('sb-last-week');
            } else if ((isBetweenStartWeek && hoverAfterSelectedFirstWeek) || (isBetweenEndWeek && !hoverAfterSelectedFirstWeek)) {
              dateElementsRight[i].parentNode.classList.add('sb-first-week');
              dateElementsRight[i].parentNode.classList.remove('sb-last-week');
            } else if ((isBetweenStartWeek && !hoverAfterSelectedFirstWeek) || (isBetweenEndWeek && hoverAfterSelectedFirstWeek)) {
              dateElementsRight[i].parentNode.classList.remove('sb-first-week');
              dateElementsRight[i].parentNode.classList.add('sb-last-week');
            } else if (isBetween || isBetweenReverse) {
              dateElementsRight[i].parentNode.classList.add('sb-between-week');
              dateElementsRight[i].parentNode.classList.remove('sb-first-week');
              dateElementsRight[i].parentNode.classList.remove('sb-last-week');
            } else {
              dateElementsRight[i].parentNode.classList.remove('sb-first-week');
              dateElementsRight[i].parentNode.classList.remove('sb-last-week');
              dateElementsRight[i].parentNode.classList.remove('sb-between-week');
            }
            dateElementsRight[i].parentNode.classList.remove('sb-hover-week');
          }
        } else {
          // only mark spans as blue text
          const pickerPanelContent = visibelPickerPanel.getElementsByClassName('el-picker-panel__content');
          const dateElementsLeft = pickerPanelContent[0].querySelectorAll('td');
          const [yearLeft, monthLeft] = pickerPanelContent[0].children[0].children[2].innerText.trim().split(' ');
          const dateElementsRight = pickerPanelContent[1].querySelectorAll('td');
          const [yearRight, monthRight] = pickerPanelContent[1].children[0].children[2].innerText.trim().split(' ');

          for (let i = 0; i < dateElementsLeft.length; i++) {
            const testDate = dateElementsLeft[i].innerText.trim();
            const testMoment = this.$moment().set('year', yearLeft).set('month', monthLeft);
            if (dateElementsLeft[i].classList.contains('prev-month')) {
              testMoment.subtract(1, 'months');
            }
            if (dateElementsLeft[i].classList.contains('next-month')) {
              testMoment.add(1, 'months');
            }
            testMoment.set('date', testDate);
            const isBetween = testMoment.isBetween(this.datePickerCurrentHoverStartWeek, this.datePickerCurrentHoverEndWeek);
            
            if (isBetween) {
              dateElementsLeft[i].parentNode.classList.add('sb-hover-week');
            } else {
              dateElementsLeft[i].parentNode.classList.remove('sb-hover-week');
            }
          }

          for (let i = 0; i < dateElementsRight.length; i++) {
            const testDate = dateElementsRight[i].innerText.trim();
            const testMoment = this.$moment().set('year', yearRight).set('month', monthRight);
            if (dateElementsRight[i].classList.contains('prev-month')) {
              testMoment.subtract(1, 'months');
            }
            if (dateElementsRight[i].classList.contains('next-month')) {
              testMoment.add(1, 'months');
            }
            testMoment.set('date', testDate);
            const isBetween = testMoment.isBetween(this.datePickerCurrentHoverStartWeek, this.datePickerCurrentHoverEndWeek);
            
            if (isBetween) {
              dateElementsRight[i].parentNode.classList.add('sb-hover-week');
            } else {
              dateElementsRight[i].parentNode.classList.remove('sb-hover-week');
            }
          }
        }
      });
    },
    onWeekPick(date) {
      if (this.weekMode) {
        this.firstWeekSelected = true;
        if (date.minDate && !date.maxDate) {
          this.tempFirstWeekStartDate = this.$moment(date.minDate).startOf('week');
          this.tempFirstWeekEndDate = this.$moment(date.minDate).endOf('week');
          this.mouseEnterDatePickerWeek();
        }
        if (date.maxDate) {
          this.$nextTick(() => {
            this.dateRange = [this.$moment(date.minDate).startOf('week'), this.$moment(date.maxDate).endOf('week')];
            this.dateRange = [...this.dateRange];
            this.$emit('datesChanged', this.dateRange);
            this.resetWeekValues();
          });
        }
      }
    },
    redrawSelection(retry = 0) {
      if (!this.weekMode || retry >= 5) {
        return;
      }

      // 1) Find a picker panel which is not hidden
      const visibelPickerPanel = this.findVisiblePickerPanel();
      if (!visibelPickerPanel) {
        setTimeout(() => { this.redrawSelection(++retry); }, 10);
        return;
      }

      // 2) Continue if picker panel is visible

      for (let i = 0; i < this.datePickerRowElements.length; i++) {
        this.datePickerRowElements[i].classList.remove('sb-first-week');
        this.datePickerRowElements[i].classList.remove('sb-last-week');
        this.datePickerRowElements[i].classList.remove('sb-between-week');
        this.datePickerRowElements[i].classList.remove('sb-hover-week');
      }
      setTimeout(() => {
        const pickerPanelContent = visibelPickerPanel.getElementsByClassName('el-picker-panel__content');
        const dateElementsLeft = pickerPanelContent[0].getElementsByClassName('available');
        const [yearLeft, monthLeft] = pickerPanelContent[0].children[0].children[2].innerText.trim().split(' ');
        const dateElementsRight = pickerPanelContent[1].getElementsByClassName('available');
        const [yearRight, monthRight] = pickerPanelContent[1].children[0].children[2].innerText.trim().split(' ');

        const startDate =
          this.tempFirstWeekStartDate
            ? this.$moment(this.tempFirstWeekStartDate)
            : this.dateRange[0]
              ? this.$moment(this.dateRange[0])
              : this.$moment().startOf('isoWeek');
        const startDateWeekEnd =
          this.tempFirstWeekStartDate
            ? this.$moment(this.tempFirstWeekStartDate).endOf('isoWeek')
            : this.dateRange[0]
              ? this.$moment(this.dateRange[0]).endOf('isoWeek')
              : this.$moment().endOf('isoWeek');
        const endDateStartWeek =
          this.tempFirstWeekEndDate
            ? this.$moment(this.tempFirstWeekEndDate).startOf('isoWeek')
            : this.dateRange[1]
              ? this.$moment(this.dateRange[1]).startOf('isoWeek')
              : this.$moment().startOf('isoWeek');
        const endDate =
          this.tempFirstWeekEndDate
            ? this.$moment(this.tempFirstWeekEndDate)
            : this.dateRange[1]
              ? this.$moment(this.dateRange[1])
              : this.$moment().endOf('isoWeek');

        for (let i = 0; i < dateElementsLeft.length; i++) {
          const testDate = dateElementsLeft[i].innerText.trim();
          const testMoment = this.$moment().set('year', yearLeft).set('month', monthLeft).set('date', testDate);
          const testMomentStartWeek = this.$moment(testMoment).startOf('isoWeek');
          const isBetween = testMoment.isBetween(startDate, endDate);
          const isBetweenReverse = testMoment.isBetween(startDate, endDate);

          // const hoverAfterSelectedFirstWeek = testMomentStartWeek.isSameOrAfter(startDate);
          const isBetweenStartWeek = testMoment.isBetween(startDate, startDateWeekEnd);
          const isBetweenEndWeek = testMoment.isBetween(endDateStartWeek, endDate);

          // console.log(testMoment.format('DD MM YYYY'), ' : ', startDate.format('DD MM YYYY'));

          if (isBetweenStartWeek && isBetweenEndWeek) {
            dateElementsLeft[i].parentNode.classList.add('sb-first-week');
            dateElementsLeft[i].parentNode.classList.add('sb-last-week');
          } else if (isBetweenStartWeek && !isBetweenEndWeek) {
            dateElementsLeft[i].parentNode.classList.add('sb-first-week');
            dateElementsLeft[i].parentNode.classList.remove('sb-last-week');
          } else if (!isBetweenStartWeek && isBetweenEndWeek) {
            dateElementsLeft[i].parentNode.classList.remove('sb-first-week');
            dateElementsLeft[i].parentNode.classList.add('sb-last-week');
          } else if (isBetween || isBetweenReverse) {
            dateElementsLeft[i].parentNode.classList.add('sb-between-week');
            dateElementsLeft[i].parentNode.classList.remove('sb-first-week');
            dateElementsLeft[i].parentNode.classList.remove('sb-last-week');
          } else {
            dateElementsLeft[i].parentNode.classList.remove('sb-first-week');
            dateElementsLeft[i].parentNode.classList.remove('sb-last-week');
            dateElementsLeft[i].parentNode.classList.remove('sb-between-week');
          }
          dateElementsLeft[i].parentNode.classList.remove('sb-hover-week');
        }

        for (let i = 0; i < dateElementsRight.length; i++) {
          const testDate = dateElementsRight[i].innerText.trim();
          const testMoment = this.$moment().set('year', yearRight).set('month', monthRight).set('date', testDate);
          const isBetween = testMoment.isBetween(startDate, endDate);
          const isBetweenReverse = testMoment.isBetween(startDate, endDate);

          // const hoverAfterSelectedFirstWeek = startDate.isSameOrAfter(endDate);
          const isBetweenStartWeek = testMoment.isBetween(startDate, startDateWeekEnd);
          const isBetweenEndWeek = testMoment.isBetween(endDateStartWeek, endDate);

          // console.log(testMoment.format('DD MM YYYY'), ' : ', endDateStartWeek.format('DD MM YYYY'), ' : ', endDate.format('DD MM YYYY'));
          
          if (isBetweenStartWeek && isBetweenEndWeek) {
            dateElementsRight[i].parentNode.classList.add('sb-first-week');
            dateElementsRight[i].parentNode.classList.add('sb-last-week');
          } else if (isBetweenStartWeek && !isBetweenEndWeek) {
            dateElementsRight[i].parentNode.classList.add('sb-first-week');
            dateElementsRight[i].parentNode.classList.remove('sb-last-week');
          } else if (!isBetweenStartWeek && isBetweenEndWeek) {
            dateElementsRight[i].parentNode.classList.remove('sb-first-week');
            dateElementsRight[i].parentNode.classList.add('sb-last-week');
          } else if (isBetween || isBetweenReverse) {
            dateElementsRight[i].parentNode.classList.add('sb-between-week');
            dateElementsRight[i].parentNode.classList.remove('sb-first-week');
            dateElementsRight[i].parentNode.classList.remove('sb-last-week');
          } else {
            dateElementsRight[i].parentNode.classList.remove('sb-first-week');
            dateElementsRight[i].parentNode.classList.remove('sb-last-week');
            dateElementsRight[i].parentNode.classList.remove('sb-between-week');
          }
          dateElementsRight[i].parentNode.classList.remove('sb-hover-week');
        }
      });
    },
    findVisiblePickerPanel() {
      const pickerPanels = document.getElementsByClassName('el-picker-panel');
      let visibleId = null;
      for (let i = 0; i < pickerPanels.length; i++) {
        const computedElementStyle = getComputedStyle(pickerPanels[i]);
        if (computedElementStyle.display !== 'none') {
          visibleId = i;
          break;
        }
      }
      if (visibleId === null) {
        return undefined;
      }

      return pickerPanels[visibleId];
    },
    shortCutDateSelect(value) {
      switch (value) {
        case 'all':
          if (this.startDate) {
            this.dateRange = [this.$moment(this.startDate).startOf('day'), this.$moment().endOf('day')];
          } else {
            this.clear();
          }
          break;
        case 'today':
          this.dateRange = [this.$moment().startOf('day'), this.$moment()];
          this.$nextTick(() => {
            this.dateRange = [this.$moment().startOf('day'), this.$moment()];
          });
          break;
        case 'last7Days':
          this.dateRange = [this.$moment().subtract(6, 'days').startOf('day'), this.$moment()];
          break;
        case 'thisWeek':
          this.dateRange = [this.$moment().startOf('isoWeek'), this.$moment().endOf('isoWeek')];
          break;
        case 'lastWeek':
          this.dateRange = [this.$moment().subtract(1, 'week').startOf('isoWeek'), this.$moment().subtract(1, 'week').endOf('isoWeek')];
          break;
        case 'last30Days':
          this.dateRange = [this.$moment().subtract(29, 'days').startOf('day'), this.$moment()];
          break;
        case 'thisMonth':
          this.dateRange = [this.$moment().startOf('month'), this.$moment().endOf('month')];
          break;
        case 'lastMonth':
          this.dateRange = [this.$moment().subtract(1, 'month').startOf('month'), this.$moment().subtract(1, 'month').endOf('month')];
          break;
        case 'last365Days':
          this.dateRange = [this.$moment().subtract(1, 'year').startOf('day'), this.$moment()];
          break;
        case 'thisYear':
          this.dateRange = [this.$moment().startOf('year'), this.$moment().endOf('year')];
          break;
        case 'lastYear':
          this.dateRange = [this.$moment().subtract(1, 'year').startOf('year'), this.$moment().subtract(1, 'year').endOf('year')];
          break;
        case 'allTime':
          this.dateRange = [this.$moment().subtract(396, 'days'), this.$moment()];
          break;
        default:
          break;
      }
    },
    setDateRange() {
      this.dateRange = [
        this.$moment(false, false).unix(this.datePickerStartDate),
        this.$moment(false, false).unix(this.datePickerEndDate),
      ];
    },
    setRangeShortcutName() {
      // this.selectedDateName = `${this.dateRange[0].format('MM/DD/YYYY')} - ${this.dateRange[1].format('MM/DD/YYYY')}`; // old code
      this.showCustom = true;
      this.selectedShortcut = {
        text: 'Custom',
        value: 'custom',
      };
    },
    clear(triggerChangedEvent = true) {
      this.triggerChangedEvent = triggerChangedEvent;
      this.selectedDateName = 'Select Date';
      this.dateRange = [];
    },
    resetWeekValues() {
      if (!this.weekMode) {
        return;
      }
      this.datePickerCurrentHoverEndWeek = null;
      this.datePickerCurrentHoverStartWeek = null;
      // clear all row classes
      this.$nextTick(() => {
        for (let i = 0; i < this.datePickerRowElements.length; i++) {
          this.datePickerRowElements[i].classList.remove('sb-first-week');
          this.datePickerRowElements[i].classList.remove('sb-last-week');
          this.datePickerRowElements[i].classList.remove('sb-between-week');
          this.datePickerRowElements[i].classList.remove('sb-hover-week');
          this.firstWeekSelected = false;
          this.tempFirstWeekStartDate = null;
          this.tempFirstWeekEndDate = null;
        }
      });
    },
    closeDatePicker() {
      this.$refs.datePicker.hidePicker();
      this.resetWeekValues();
    },
    closeMonthPicker() {
      this.$refs.datePickerMonth.hidePicker();
    },
  },
};
</script>

<style lang="scss" scoped>
.SB-shadow {
  box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
}

.SB-datepicker {
  width: fit-content;
  display: flex;
  // box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
  border-radius: 5px;
  position: relative;

  ::v-deep .multiselect {
    width: fit-content;
    display: inline-block;
    // box-shadow: none;
    z-index: 10;
    min-height: 30px !important;
    position: relative;

    &:after {
      content: "";
      background-color: $white;
      display: block;
      height: 28px;
      width: 5px;
      position: absolute;
      top: 1px;
      right: -5px;
      border-left: 1px solid $greyBorder;
    }

    &:focus {
      // box-shadow: none !important;
    }

    &:hover {
      // box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
      z-index: 1;
    }

    .multiselect__select {
      transition: none;
      right: 0;
      margin-right: 14px;
      width: calc(100% - 14px);
      height: 30px;
    }

    .multiselect__tags {
      padding-right: 32px;
      padding-left: 15px;
      border: solid 1px $greyBorder;
      border-right: none;
      border-bottom-right-radius: 0 !important;
      border-top-right-radius: 0 !important;
      min-height: 30px;

      .multiselect__single {
        padding-right: 0;
        font-weight: 500 !important;
      }
    }

    .multiselect__content {
      padding: 8px 0 !important;
    }

    &.flex-options .multiselect__option {
      padding: 10px 10px !important;

      span {
        color: $blackPrimary;
      }

      .sb-icon {
        margin-right: 15px;
        margin-left: 5px;
      }
    }
    div.multiselect__content-wrapper {
      max-height: 310px !important;
      width: fit-content;
    }
  }

  ::v-deep .el-date-editor {
    height: 30px !important;
    width: fit-content !important;
    margin: 0 !important;
    padding: 0 15px !important;
    border-radius: 5px !important;
    border-color: $borderGray;
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    overflow: hidden;
    z-index: 10;

    &:hover {
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
      z-index: 1;
    }

    .el-input__icon {
      display: none;
    }

    input {
      box-shadow: none;
    }

    .el-range-input {
      font-size: 14px;
      font-weight: 500;
      color: $blackPrimary;

      &::placeholder {
        color: $blackUltralight !important;
      }

      &:first-of-type {
        text-align: right;
      }

      &:last-of-type {
        text-align: left;
      }

      &:focus {
        box-shadow: none !important;
      }
    }

    .el-range-separator {
      width: 10px;
      padding: 0;
      text-align: center;
    }
  }

  .datepicker-active-shadow {
    position: absolute;
    right: 0;
    top: 0;
    width: 210px;
    height: 30px;
    border-radius: 5px;
    box-shadow: 0 0 0 3px #bfdeff !important;
    display: none;

    &.is-active {
      display: block;
    }
  }
}
</style>

<style lang="scss">
// TODO check unused
body.date-picker-initial-mode.date-picker-week
.el-picker-panel {
  .el-date-table__row {
    &:hover {
      td.available {
        // color: $bluePrimary;
      }
    }
  }
}

body:not(.date-picker-initial-mode).date-picker-week
.el-picker-panel {
  .el-date-table__row {
    td.sb-between-week div {
      background-color: #f2f6fc !important;
      // TODO add round for start i end
    }

    &:hover {
      td:not(.prev-month):not(.next-month) div {
        background-color: #f2f6fc !important;
        // TODO add round for start i end
      }
    }
  }
}

body.date-picker-week
.el-picker-panel {
  // RESETS
  .in-range div,
  .start-date div,
  .end-date div {
    background-color: unset;
    margin-right: unset;
    margin-left: unset;
    border-top-right-radius: unset;
    border-bottom-right-radius: unset;
    border-top-left-radius: unset;
    border-bottom-left-radius: unset;
    &:hover {
      background-color: unset;
    }
  }

  // RESETS
  .start-date span,
  .end-date span {
    background-color: unset !important;
    color: $blackPrimary !important;
    // &:hover {
    //   background-color: unset;
    // }
  }

  // RESETS
  td.today span,
  td.today.start-date span,
  td.today.end-date span,
  td.today.start-date.end-date span {
    color: $bluePrimary !important;
  }

  // RESETS
  .available:hover {
    color: $blackPrimary;
  }

  .sb-first-week,
  .sb-last-week,
  .sb-between-week {
    td:not(.prev-month):not(.next-month) div {
      background-color: #f2f6fc !important;
      // TODO add round for start i end
    }
  }

  .sb-between-week {
    td.prev-month div,
    td.next-month div {
      border-top: 1px solid #f2f6fc;
      border-bottom: 1px solid #f2f6fc;
      padding-top: 2px;
    }

    td:first-of-type.prev-month div {
      border-left: 1px solid #f2f6fc;
      border-top-left-radius: 15px;
      border-bottom-left-radius: 15px;
    }

    td:last-of-type.next-month div {
      border-right: 1px solid #f2f6fc;
      border-top-right-radius: 15px;
      border-bottom-right-radius: 15px;
    }
  }

  .sb-first-week,
  .sb-last-week {
    td.prev-month div,
    td.next-month div {
      border-top: 1px solid $bluePrimary;
      border-bottom: 1px solid $bluePrimary;
      padding-top: 2px;
    }

    td:first-of-type.prev-month div {
      border-left: 1px solid $bluePrimary;
      border-top-left-radius: 15px;
      border-bottom-left-radius: 15px;
    }

    td:last-of-type.next-month div {
      border-right: 1px solid $bluePrimary;
      border-top-right-radius: 15px;
      border-bottom-right-radius: 15px;
    }
  }

  .sb-hover-week td:not(.prev-month):not(.next-month) {
    color: $bluePrimary !important;
  }

  .sb-first-week,
  .sb-last-week {
    td:not(.prev-month):not(.next-month) div span {
      background: $bluePrimary !important;
      color: $white !important;
      border-radius: 0 !important;
      width: 103% !important;
      left: 50%;
      margin: -3px 0;
      height: 30px;
      line-height: 30px;
    }
  }

  .sb-first-week,
  .sb-last-week {
    td:first-of-type:not(.prev-month):not(.next-month) div {
      margin-left: 5px !important;
      border-top-left-radius: 15px !important;
      border-bottom-left-radius: 15px !important;

      span {
        width: 34px !important;
        border-top-left-radius: 15px !important;
        border-bottom-left-radius: 15px !important;
        left: 51%;
        width: 42px !important;
      }
    }

    td:last-of-type:not(.prev-month):not(.next-month) div {
      span {
        border-top-right-radius: 15px !important;
        border-bottom-right-radius: 15px !important;
        // padding-left: 10px !important;
        left: 47% !important;
        width: 44px !important;
      }
    }
  }

  .sb-first-week {
    td:first-of-type:not(.prev-month):not(.next-month) div {
      margin-left: 0px !important;
      border-top-left-radius: 15px !important;
      border-bottom-left-radius: 15px !important;
    }
  }

  .sb-last-week {
    &:not(.sb-first-week) td:first-of-type:not(.prev-month):not(.next-month) div {
      margin-left: 0px !important;
      border-top-left-radius: 0px !important;
      border-bottom-left-radius: 0px !important;
    }
    td:last-of-type:not(.prev-month):not(.next-month) div {
      margin-right: 0px !important;
      border-top-right-radius: 15px !important;
      border-bottom-right-radius: 15px !important;
    }
  }

  // .sb-end-week div {
  //   margin-right: 5px;
  //   border-top-right-radius: 15px;
  //   border-bottom-right-radius: 15px;
  // }
}
</style>
